import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Checkbox, Grid, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import InfoIcon from "@mui/icons-material/Info";
import { CancelButton, SaveAndUpdate, UploadButton } from "../../utils/commonComponent/Custom/Button";
import { moduleHierarchy, moduleList, userRoles, userStatus, userTypes } from "../../utils/commonComponent/userConstant";
import { fetchQuery } from "../../utils/commonComponent/fetched";
import { AutoCompletes, TextFields } from "../../utils/commonComponent/Custom/custominput";
import Text from "../../utils/commonComponent/Custom/text";
import { BASE_URL, DISABLED_MODULE, DOMAIN } from "../../global";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { fetchRBACData, getPermissions } from "../rbac/hasAccess";
import { validateRequiredFields } from "../../utils/commonComponent/errorAlert";
import { DisciplineOptions } from "../disciplineMaster/disiplineConst";

// Filter out disabled modules
const EnabledModules = moduleList.filter(
  (module) => !DISABLED_MODULE.includes(module.label),
);

// File Upload Field
const FileUploadComponent = ({
  fileUploadResponse,
  setFileUploadResponse,
  label,
}) => {
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    // added validation only
    if (file && /[&#$*]/.test(file.name)) {
      toast.warning(
        "File name contains special characters. Please rename the file and upload again.",
      );
      return;
    }
    const data = new FormData();
    data.append("file", file);

    axiosWithToken
      .post(BASE_URL + `storage/upload`, data)
      .then((res) => {
        if (res.status === 200) {
          setFileUploadResponse(
            res.data?.[0]?.path ? `${DOMAIN}/${res.data?.[0]?.path}` : null,
          );
          toast.success("File Uploaded Successfully!");
        }
      })
      .catch((err) => {
        if (err.message !== "request_aborted")
          toast.error("Something Went Wrong!");
      });
  };

  return (
    <div className="w-full mx-5">
      <UploadButton
        label={label}
        onChange={handleFileUpload}
        fileUrl={fileUploadResponse}
        onRemove={() => setFileUploadResponse(null)}
      />
    </div>
  );
};

export default function EditUserMaster(props) {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const navigate = useNavigate();
  const [inputStates, setInputStates] = useState({
    userName: "",
    password: "",
    passVal: [0, 0, 0, 0, 0],
    type: null,
    roles: null,
    selectedModules: [],
    employeeId: null,
    disciplineId: null,
    groupId: null,
    clientId: null,
    userStatus: 1,
  });

  const [isHovered, setIsHovered] = useState(false);
  const [modules, setModules] = useState(EnabledModules);
  const [fileUploadResponse, setFileUploadResponse] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [rollList, setrollList] = useState([]);
  const [disgroupList, setDisGroupList] = React.useState([]);
  const [clientList, setClientList] = useState([]);
  const [settings, setSettings] = useState([]);

  const updateInputObject = (key, value) =>
    setInputStates((prev) => ({ ...prev, [key]: value }));

  const fetchDisciplineDetails = async () => {
    try {
      const gropuList = await fetchQuery(
        `select * from discipline where name in (${inputStates.disciplineId})`,
      );
      setDisGroupList(gropuList);
    } catch (error) {
      console.error("Error fetching Discipline data:", error);
    }
  };

  const onSubmit = async () => {
    const jsonData = {
      userName: inputStates.userName,
      password: inputStates.password,
      moduleAccess:
        inputStates.selectedModules.map((m) => m.id).join(",") || null,
      roles: inputStates.roles?.join(",") || null,
      type: inputStates.type || null,
      usersign: fileUploadResponse || null,
      status: 1,
      userStatus: inputStates.userStatus ?? 1,
      employeeId: inputStates.employeeId || null,
      disciplineId: inputStates.disciplineId?.join(",") || null,
      groupId: inputStates.groupId?.join(",") || null,
      clientId: inputStates.clientId || null,
    };
    // show alert
    const fieldsToValidate = [
      // emp is not required when type is 13
      ...(inputStates.type !== 13
        ? [{ value: inputStates.employeeId, label: "Employee selection Required" }]
        : []),
      { value: inputStates.userName, label: "UserName Required" },
      { value: inputStates.type, label: "Type selection required" },
      // client required when type is 13
      ...(inputStates.type === 13
        ? [{ value: inputStates.clientId, label: "Client selection Required" }]
        : []),
    ];
    if (!validateRequiredFields(fieldsToValidate)) {
      return;
    }

    const url = BASE_URL + `userMaster`;

    try {
      params.id == 0
        ? await axiosWithToken.post(url, jsonData)
        : await axiosWithToken.patch(url + `/${params.id}`, jsonData);

      toast.success(
        params.id === "0"
          ? "User Created Successfully!"
          : "Updated Successfully!",
      );
      if (params.id == 0) {
        setTimeout(() => navigate("/master/usersList"), 1000);
      } else {
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.log("Something Went Wrong!");
    }
  };

  const initModules = () => {
    let filteredModules = EnabledModules?.filter((module) => {
      // Include modules not in any child or parent modules themselves
      if (
        !Object.values(moduleHierarchy).some(
          (range) => module.id >= range.min && module.id <= range.max,
        )
      ) {
        return true;
      }

      // Include child modules if their parent module is selected
      return inputStates.selectedModules.some((selectedModule) => {
        const range = moduleHierarchy[selectedModule.id];
        return range && module.id >= range.min && module.id <= range.max;
      });
    });
    setModules(filteredModules);
  };

  useEffect(() => {
    initModules();
  }, [inputStates.selectedModules]);

  const fetchUserDetails = async () => {
    try {
      const [user] = await fetchQuery(
        `select * from userMaster where id = ${params.id}`,
      );
      const password = user.password || "";

      const passVal = [
        /[a-z]/.test(password) ? 1 : 0,
        /[A-Z]/.test(password) ? 1 : 0,
        /[0-9]/.test(password) ? 1 : 0,
        password.length >= 8 ? 1 : 0,
        password.length <= 32 ? 1 : 0,
      ];

      updateInputObject("userName", user.userName);
      updateInputObject("password", password);
      updateInputObject("passVal", passVal);
      setFileUploadResponse(user?.usersign);

      updateInputObject("employeeId", user.employeeId);
      updateInputObject(
        "disciplineId",
        user.disciplineId
          ? user.disciplineId.split(",").map((id) => Number(id.trim()))
          : null,
      );

      updateInputObject(
        "groupId",
        user.groupId
          ? user.groupId.split(",").map((id) => Number(id.trim()))
          : null,
      );
      // Handle single type selection
      updateInputObject(
        "type",
        user.type ? userTypes.find((u) => u.id == user.type)?.id : null,
      );

      updateInputObject(
        "roles",
        user.roles
          ? user.roles.split(",").map((id) => Number(id.trim()))
          : null,
      );

      updateInputObject(
        "selectedModules",
        user.moduleAccess
          ? user.moduleAccess
              ?.split(",")
              ?.map((id) =>
                rollList.find((mod) => mod.id == parseInt(id.trim())),
              )
              .filter(Boolean)
          : [],
      );

      updateInputObject(
        "userStatus",
        user.userStatus ? Number(user.userStatus) : 1,
      );

      updateInputObject("clientId", user.clientId || null);
    } catch (error) {
      console.error("Error fetching User data:", error);
    }
  };

  const fetchEmployeesData = async () => {
    try {
      const Employees = await fetchQuery(
        `select id,employeesCode,name from employees where status = 1`,
      );
      setEmployeeList(Employees);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  const fetchClientList = async () => {
    try {
      const clients = await fetchQuery(
        `select id, companyName from clientMaster where status = 1`,
      );
      setClientList(clients);
    } catch (error) {
      console.error("Error fetching client data:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetchQuery(`select * from settings where status = 1`);
      setSettings(res);
    } catch (err) {
      console.error(err);
    }
  };

  const settingsMap = useMemo(() => {
    return settings.reduce((acc, item) => {
      acc[item.keyName] = item.status;
      return acc;
    }, {});
  }, [settings]);

  const filteredUserRoles = useMemo(() => {
    return userRoles.filter((role) => {
      const key = `activeRole${role.id}`;
      return settingsMap[key] === 1;
    });
  }, [settingsMap]);

  const handlePasswordChange = (val) => {
    const passVal = [
      /[a-z]/.test(val) ? 1 : 0,
      /[A-Z]/.test(val) ? 1 : 0,
      /[0-9]/.test(val) ? 1 : 0,
      val.length >= 8 ? 1 : 0,
      val.length <= 32 ? 1 : 0,
    ];
    updateInputObject("password", val);
    updateInputObject("passVal", passVal);
  };

  useEffect(() => {
    fetchEmployeesData();
    fetchClientList();
    fetchRBACData(false, setrollList);
    fetchSettings();
  }, []);

  useEffect(() => {
    if (params.id !== "0") fetchUserDetails();
  }, [params, rollList]);

  useEffect(() => {
    if (!params.id || !inputStates.groupId || !disgroupList.length) return;
    if (typeof inputStates.groupId === "string") {
      const groupArray = inputStates.groupId
        ?.split(",")
        ?.map((id) =>
          disgroupList.find((mod) => mod.id === parseInt(id.trim())),
        )
        ?.filter(Boolean);

      updateInputObject("groupId", groupArray);
    }
  }, [disgroupList, inputStates.groupId]);

  useEffect(() => {
    if (inputStates.disciplineId) {
      fetchDisciplineDetails();
    }
  }, [inputStates.disciplineId]);

  return (
    <div>
      <Text variant="h3" bold className="text-blue-600">
        {params.id == 0 ? "Create New User" : `Update ${inputStates?.userName}`}
      </Text>
      <br />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="w-full">
          <Autocomplete
            sx={{ width: "100%" }}
            size="small"
            options={employeeList}
            getOptionLabel={(option) =>
              `${option.name} (${option.employeesCode})`
            }
            value={
              employeeList.find((emp) => emp.id === inputStates.employeeId) ||
              null
            }
            onChange={(e, val) =>
              updateInputObject("employeeId", val ? val.id : null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={inputStates.type === 13 ? "Select Employee (optional)" : "Select Employee *"}
                placeholder="Employee"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </div>

        <div className="w-full">
          <TextFields
            label="Username *"
            value={inputStates.userName}
            onChange={(e) => updateInputObject("userName", e.target.value)}
          />
        </div>

        <div className="w-full">
          <TextFields
            className="relative"
            label="Password *"
            value={inputStates.password}
            onChange={(e) => handlePasswordChange(e.target.value)}
          />
          {/* <InfoIcon
            style={{ color: "gray", position: "absolute", height: "20px" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          /> */}
          {/* {isHovered && ( */}
          <div className="password-validation-info">
            {[
              ["A lowercase letter", inputStates.passVal[0]],
              ["A capital letter", inputStates.passVal[1]],
              ["A number", inputStates.passVal[2]],
              ["Minimum 8 characters", inputStates.passVal[3]],
              ["Maximum 32 characters", inputStates.passVal[4]],
            ].map(([text, valid], i) => (
              <p key={i} className={valid ? "text-green-600" : "text-red-600"}>
                {text}
              </p>
            ))}
          </div>
          {/* )} */}
        </div>
        <FileUploadComponent
          fileUploadResponse={fileUploadResponse}
          setFileUploadResponse={setFileUploadResponse}
          label="Upload Sign"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
        <div className="w-full">
          <AutoCompletes
            label="User Types *"
            options={userTypes}
            value={inputStates.type}
            onChange={(val) => {
              updateInputObject("type", val);
              updateInputObject("roles", null);
              updateInputObject("disciplineId", null);
              updateInputObject("groupId", null);
              updateInputObject("clientId", null);
            }}
            isWidth
            valueKey="id"
            labelKey="label"
            // disabled={params.id != 0}
          />
        </div>

        {/* Client dropdown */}
        {inputStates?.type === 13 && (
          <div className="w-full">
            <Autocomplete
              sx={{ width: "100%" }}
              size="small"
              options={clientList}
              getOptionLabel={(option) => option.companyName || ""}
              value={
                clientList.find((c) => c.id === inputStates.clientId) || null
              }
              onChange={(e, val) =>
                updateInputObject("clientId", val ? val.id : null)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Client *"
                  placeholder="Client *"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </div>
        )}

        {[1, 2, 3, 4, 11].includes(inputStates?.type) && (
          <div className="w-full">
            <AutoCompletes
              label="Roles"
              options={filteredUserRoles}
              value={inputStates.roles}
              onChange={(val) => {
                updateInputObject("roles", val);
                const prevRoles = inputStates.roles || [];
                // Check if 2 was previously selected but now removed
                // if (prevRoles.includes(2) && !val?.includes(2)) {
               if ([2, 3, 4].some(r => prevRoles.includes(r)) && !val?.some(r => [2, 3, 4].includes(r))) {
                  updateInputObject("disciplineId", null);
                  updateInputObject("groupId", null);
                }
              }}
              isWidth
              multiple
              valueKey="id"
              labelKey="label"
            />
          </div>
        )}

        {inputStates?.roles?.some(role => [2, 3, 4].includes(role)) && (
          <>
            <div className="w-full">
              <AutoCompletes
                label="Discipline Name"
                options={[{ id: 0, label: "Select All" }, ...DisciplineOptions]}
                value={inputStates.disciplineId}
                onChange={(val) => {
                  if (val.includes(0)) {
                    // "Select All" clicked
                    updateInputObject(
                      "disciplineId",
                      DisciplineOptions.map((d) => d.id),
                    );
                  } else {
                    updateInputObject("disciplineId", val);
                  }
                }}
                isWidth
                multiple
                valueKey="id"
                labelKey="label"
                limitTags={2}
              />
            </div>

            <div className="w-full">
              <AutoCompletes
                label="Group Name"
                options={[{ id: 0, groupName: "Select All" }, ...disgroupList]}
                value={inputStates.groupId}
                onChange={(val) => {
                  if (val.includes(0)) {
                    // Select All clicked
                    updateInputObject(
                      "groupId",
                      disgroupList.map((g) => g.id),
                    );
                  } else {
                    updateInputObject("groupId", val);
                  }
                }}
                isWidth
                multiple
                valueKey="id"
                labelKey="groupName"
                limitTags={2}
              />
            </div>
          </>
        )}

        {/* Define filtered list outside or inline */}
        {(() => {
          const allowedRoles = ["l1", "hod", "h1", "h2", "l2"];

          const filteredRollList =
            inputStates?.type == 1 || inputStates?.type == 2
              ? rollList.filter((r) =>
                  allowedRoles.some((role) =>
                    r.roleName?.toLowerCase().includes(role.toLowerCase()),
                  ),
                )
              : rollList;

          return (
            <div className="w-full md:col-span-2">
              <Autocomplete
                multiple
                size="small"
                options={[
                  { id: 0, roleName: "Select All" },
                  ...[...filteredRollList].sort((a, b) =>
                    (a.roleName || "").localeCompare(b.roleName || ""),
                  ),
                ]}
                value={inputStates.selectedModules}
                disableCloseOnSelect
                getOptionLabel={(option) => option?.roleName || ""}
                renderOption={(props, option, { selected }) => (
                  <li
                    {...props}
                    style={{ paddingLeft: option.id > 50 ? "1rem" : "0" }}
                  >
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.roleName}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select modules (optional) *"
                    placeholder="Select multiple modules"
                  />
                )}
                limitTags={2}
                onChange={(e, val) => {
                  if (val.some((v) => v.id == 0)) {
                    const allSelected =
                      inputStates.selectedModules.length ===
                      filteredRollList.length;
                    updateInputObject(
                      "selectedModules",
                      allSelected ? [] : filteredRollList,
                    );
                  } else {
                    updateInputObject("selectedModules", val);
                  }
                }}
              />
            </div>
          );
        })()}
        {/* User Status */}
        <div className="w-full">
          <AutoCompletes
            label="User Status *"
            options={userStatus}
            value={inputStates.userStatus}
            onChange={(val) => updateInputObject("userStatus", val)}
            isWidth
            valueKey="id"
            labelKey="label"
          />
        </div>
      </div>

      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.allAccess) && (
          <SaveAndUpdate
            label={`${params.id !== "0" ? "Update" : "Save"} ${
              inputStates.userName
            }`}
            onClick={onSubmit}
            disabled={
              !inputStates.userName ||
              !inputStates.password ||
              inputStates.passVal.reduce((a, b) => a + b, 0) < 5 ||
              (params.id != 0 && !inputStates.type) ||
              (inputStates.type !== 13 && !inputStates.employeeId) ||
              (inputStates.type === 13 && !inputStates.clientId)
            }
          />
        )}
      </div>
    </div>
  );
}
